/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import React from 'react';
import { Paper, Box, Typography } from '@mui/material';
import { primaryKeyForSection } from '../Post/PostLayer';
import { useAtomValue, useSetAtom } from 'jotai';
import { exportLegendDataAtom } from '../map/atoms';
import { groupVisibilityAtom, leafVisibilityAtom } from '../map/layerControls/atoms';
import { isLeafShownIn, leafKeyForLayerId } from '../map/layerControls/layerRegistry';
import countyDict from '../Incident/Incident_page_dictionaries/County_dict.json';
import communicationsDict from '../Incident/Incident_page_dictionaries/Communication_dict.json';
import commoditiesDict from '../Incident/Incident_page_dictionaries/Commodities_dict.json';
import energyDict from '../Incident/Incident_page_dictionaries/Energy_dict.json';
import foodWaterDict from '../Incident/Incident_page_dictionaries/FoodWaterShelter_dict.json';
import healthDict from '../Incident/Incident_page_dictionaries/HealthAndMedical_dict.json';
import nonResidentialDict from '../Incident/Incident_page_dictionaries/NonResidentialStructures_dict.json';
import populationExposureDict from '../Incident/Incident_page_dictionaries/PopulationExposure_dict.json';
import residentialStructuresDict from '../Incident/Incident_page_dictionaries/ResidentialStructures_dict.json';
import transportationDict from '../Incident/Incident_page_dictionaries/Transportation_dict.json';
import grantsDict from '../Incident/Incident_page_dictionaries/Grants_dict.json';
import wowDict from '../Incident/Incident_page_dictionaries/Wow_dict.json';
import { useMatch } from 'react-router-dom';
import { selectedRunTimeAtom } from '../Incident/atoms';

export const keyToLabel: Record<string, Record<string, string>> = {
  county: countyDict,
  communications: communicationsDict,
  commodities: commoditiesDict,
  energy: energyDict,
  foodWaterShelter: foodWaterDict,
  healthMedical: healthDict,
  transportation: transportationDict,
  residential: residentialStructuresDict,
  population: populationExposureDict,
  nonResidentialStructures: nonResidentialDict,
  declarations: grantsDict,
  'wow-perimeter': wowDict,
  'wow-forecast-perimeter': wowDict,
  'wow-observed-perimeter': wowDict,
};

export type LegendProps = {
  id: string;
  title?: string;
  items: { label: string; color: string; value?: string | number }[];
  /** Header only (title, model and run time) with no swatch rows, for a layer
   * drawn beneath the layer whose legend follows. */
  compact?: boolean;
  /** The layer-list leaf whose eyes govern this legend, for a legend whose id
   * is not the prefix of its layer's id (see leafKeyForLayerId). */
  leafKey?: string;
};

/**
 * What a layer's colors mean: the swatch rows and, for an incident section,
 * the model run they were drawn from. Display only -- every control that acts
 * on the map lives in the Map Controls panel (MapControls.tsx).
 */
export const Legend = React.memo((props: LegendProps) => {
  const isIncidentPage = useMatch('/:incidentId/:sectionId/:viewType/:attributeId?');
  const isIncidentNrccPage = useMatch('/wowcast/:incidentId/:sectionId');
  const incidentId = isIncidentNrccPage?.params.incidentId ?? isIncidentPage?.params.incidentId;

  const selectedRuntime = useAtomValue(selectedRunTimeAtom(`${incidentId}-${props.id}`));
  const primaryKey = useAtomValue(primaryKeyForSection(props.id));
  const setExportLegendData = useSetAtom(exportLegendDataAtom);

  // The PDF export lists only the layers drawn on the map: a legend whose
  // layer the layer list has hidden stays on screen (its eye is right there)
  // but leaves the export. A legend id no leaf claims is not the layer list's
  // to hide, and a compact legend describes a layer drawn beneath another
  // whose own legend follows, so neither is exported.
  const leafVisibility = useAtomValue(leafVisibilityAtom);
  const groupVisibility = useAtomValue(groupVisibilityAtom);
  const leafKey = props.leafKey ?? leafKeyForLayerId(props.id);
  const shown = leafKey == null || isLeafShownIn(leafKey, leafVisibility, groupVisibility);
  const exported = shown && !props.compact;

  const title = keyToLabel?.[props.id]?.[primaryKey] ?? props?.title ?? 'Legend';
  const model =
    selectedRuntime?.comments != null &&
    selectedRuntime.comments !== new Date(selectedRuntime.time).toLocaleString()
      ? selectedRuntime.comments
      : undefined;
  const updatedOn =
    selectedRuntime?.time != null ? new Date(selectedRuntime.time).toLocaleString() : undefined;

  React.useEffect(() => {
    if (!exported) {
      return;
    }
    setExportLegendData(prev => [
      ...prev.filter(g => g.title !== title),
      {
        title,
        items: props.items.map(i => ({ label: i.label, color: i.color })),
        model,
        updatedOn,
      },
    ]);
    return () => {
      setExportLegendData(prev => prev.filter(g => g.title !== title));
    };
  }, [exported, title, props.items, model, updatedOn, setExportLegendData]);

  const header = [
    title,
    model != null ? `Model:\n${model}` : null,
    updatedOn != null ? `Updated On\n${updatedOn}` : null,
  ]
    .filter(line => line != null)
    .join('\n\n');

  return (
    <Paper elevation={0} sx={{ overflow: 'hidden' }}>
      <Box sx={{ minWidth: 228, maxWidth: 250 }}>
        {!props.compact && (
          <Box sx={{ p: 1, overflowY: 'auto', maxHeight: 220 }}>
            {props.items.map((item, index) => (
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'row',
                  fontSize: 12,
                  marginBottom: 0.75,
                }}
                key={index}
              >
                <Box
                  sx={{
                    background: item.color,
                    height: 14,
                    width: 14,
                    maxHeight: 14,
                    maxWidth: 14,
                    minHeight: 14,
                    minWidth: 14,
                    borderRadius: '2px',
                    marginRight: 1,
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <span>{item.label}</span>
                  {item.value && <b>{item.value}</b>}
                </Box>
              </Box>
            ))}
          </Box>
        )}
        <Box sx={{ p: 1, backgroundColor: 'rgb(245, 250, 252)' }}>
          <Typography
            sx={{
              fontWeight: 'bold',
              display: 'block',
              lineHeight: 1,
              whiteSpace: 'pre-line',
            }}
            variant="overline"
          >
            {header}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
});
